/**
 * Accès Firestore aux personnages VtM, en API modulaire.
 * Partage l'app Firebase initialisée par la couche compat (même session, même cache).
 */
import {getFirestore, doc, setDoc, getDoc, updateDoc, deleteDoc, deleteField, onSnapshot, serverTimestamp, collection, query, where} from 'firebase/firestore';
import {getStorage, ref, deleteObject} from 'firebase/storage';

const charactersRef = (uid) => doc(getFirestore(), 'characters', uid);

/**
 * Crée le document d'un personnage (cf. createVtmCharacter).
 * `active: true` : même convention que les personnages Aria pour apparaître dans le roster.
 */
export const saveNewCharacter = (character) => setDoc(charactersRef(character.uid), {
  ...character,
  active: true,
  createdAt: serverTimestamp(),
  lastUpdateAt: serverTimestamp(),
});

/**
 * Écoute un personnage en temps réel.
 * @returns {Function} désabonnement, à appeler dans le cleanup du useEffect
 */
export const watchCharacter = (uid, onChange, onError) => onSnapshot(
  charactersRef(uid),
  snapshot => onChange(snapshot.exists() ? snapshot.data() : null),
  onError,
);

/**
 * Met à jour des champs de la fiche VtM.
 * @param {String} uid
 * @param {Object} fields ex : {hunger: 3, willpower: {superficial: 2, aggravated: 0}}
 */
export const updateSheet = (uid, fields) => updateDoc(charactersRef(uid), {
  ...Object.entries(fields).reduce((acc, [key, value]) => ({...acc, [`vtm.${key}`]: value}), {}),
  lastUpdateAt: serverTimestamp(),
});

/** Enregistre le brouillon de l'assistant de création (nom + fiche complète). */
export const saveDraft = (uid, {name, vtm}) => updateDoc(charactersRef(uid), {
  name,
  vtm,
  lastUpdateAt: serverTimestamp(),
});

/** Change le statut (brouillon → à valider → validé). */
export const updateStatus = (uid, status) => updateDoc(charactersRef(uid), {
  status,
  lastUpdateAt: serverTimestamp(),
});

/**
 * Écoute tous les personnages actifs d'une campagne (vue MJ).
 * @returns {Function} désabonnement
 */
export const watchCampaignCharacters = (campaignUid, onChange, onError) => onSnapshot(
  query(collection(getFirestore(), 'characters'), where('idCampaign', '==', campaignUid), where('active', '==', true)),
  snapshot => onChange(snapshot.docs.map(item => item.data()).filter(character => character.vtm)),
  onError,
);

/** Met à jour des champs communs du personnage (nom, portrait). */
export const updateCharacter = (uid, fields) => updateDoc(charactersRef(uid), {
  ...fields,
  lastUpdateAt: serverTimestamp(),
});

/** Lit un personnage une fois (page d'attribution). */
export const getCharacter = async (uid) => {
  const snapshot = await getDoc(charactersRef(uid));
  return snapshot.exists() ? snapshot.data() : null;
};

/** Enregistre le jeton du lien d'attribution d'un personnage repris par le MJ. */
export const setClaimToken = (uid, claimToken) => updateDoc(charactersRef(uid), {claimToken});

/** Le joueur récupère le personnage : il en devient propriétaire, le lien ne sert plus. */
export const claimCharacter = (uid, idUser) => updateDoc(charactersRef(uid), {
  idUser,
  claimToken: deleteField(),
  lastUpdateAt: serverTimestamp(),
});

/**
 * Suppression définitive (fiche + portrait). Les jets déjà dans le chat restent.
 * Le portrait peut ne pas exister : son absence n'empêche pas la suppression.
 */
export const deleteCharacter = async (uid) => {
  await deleteDoc(charactersRef(uid));
  try {
    await deleteObject(ref(getStorage(), `charactersPictures/${uid}.png`));
  } catch (e) {
    if (e.code !== 'storage/object-not-found') console.log(e);
  }
};
